/*!
 * @utiljs/is 0.7.4 (http://git.sankuai.com/projects/WM/repos/utiljs/browse/is/)
 * API http://git.sankuai.com/projects/WM/repos/utiljs/browse/is/doc/api.md
 * Copyright 2017-2019 美团mfe. All Rights Reserved
 * Licensed under MIT (http://git.sankuai.com/projects/WM/repos/utiljs/browse/is/LICENSE)
 */

import { type } from '@utiljs/type';

function isInRange(x, min, max) {
  x = +x;
  min = +min;
  max = +max; // x 不是数字，则返回false

  if (isNaN(x)) return false; // min 或 max 不传，则认为不设置下限或上限

  return (!isNaN(min) ? x >= min : true) && (!isNaN(max) ? x <= max : true);
}
function isDecimalLenInRange(x, max) {
  var min = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  x = Number(x);

  if (!isNaN(x)) {
    var decimal = x.toString().split('.')[1];
    return isInRange(decimal ? decimal.length : 0, min, max);
  }

  throw new TypeError('TypeError');
}
function isStrLenInRange(x, max) {
  var min = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  return isInRange(String(x).length, min, max);
}
function isNumber(x, min, max) {
  return type(x) === 'number' && isInRange(x, min, max);
}
function isInteger(x, min, max) {
  return parseInt(x, 10) === x && isInRange(x, min, max);
}
function isInt(x) {
  // -2^31 ~ 2^31-1
  return isInteger(x, -2147483648, 2147483647);
}
function isBoolean(x) {
  return type(x) === 'boolean';
}
function isString(x) {
  return type(x) === 'string';
}
function isEmptyString(x) {
  if (!isString(x)) return false;
  return x.replace(/(^\s*|\s*$)/g, '') === '';
}
function isNull(x) {
  return type(x) === 'null';
}
function isUndefined(x) {
  return type(x) === 'undefined';
}
function isObject(x) {
  return type(x) === 'object';
}
function isFunction(x) {
  return type(x) === 'function';
}
var isArray = isFunction(Array.isArray) ? Array.isArray : function isArray(x) {
  return type(x) === 'array';
};
function isMobilephoneNumber(num) {
  num = String(num);
  return /^1[345789]\d{9}$/.test(num);
}
function isTelephoneNumber(num) {
  num = String(num);
  return /^(\d{3,4})-\d{7,8}$/.test(num);
}
function isPhoneNumber(num) {
  return isMobilephoneNumber(num) || isTelephoneNumber(num);
}
function isPhoneNumberHK(num) {
  num = String(num);
  return /^[1-9]\d{7}$/.test(num);
}
function isIdCard(cardNumber) {
  cardNumber = String(cardNumber);
  return /(^\d{15}$)|(^\d{17}([0-9]|X)$)/.test(cardNumber);
}
function isIdCardHK(cardNumber) {
  cardNumber = String(cardNumber);
  return /^((\s?[A-Za-z])|([A-Za-z]{2}))\d{6}((\([0-9aA]\))|([0-9aA]))$/.test(cardNumber);
}
function isBankCard(bankCardNumber) {
  bankCardNumber = String(bankCardNumber).replace(/(^\s*)|(\s*$)/g, '');
  return /^[1-9]([0-9]{15,18})$/.test(bankCardNumber);
} // map 转 array

function mapToArray(map) {
  var index = -1;
  var result = Array(map.size);
  map.forEach(function (value, key) {
    result[++index] = [key, value];
  });
  return result;
} // set 转 array


function setToArray(set) {
  var index = -1;
  var result = Array(set.size);
  set.forEach(function (value) {
    result[++index] = value;
  });
  return result;
} // 比较array


function equalArrays(value, other) {
  if (value.length !== other.length) {
    return false;
  }

  var index = -1;
  var result = true;

  while (++index < value.length) {
    if (!(value[index] === other[index] || isEqual(value[index], other[index]))) {
      result = false;
      break;
    }
  }

  return result;
}

function getObjectKeys(obj) {
  var keyList = [];

  for (var objkey in obj) {
    if (obj.hasOwnProperty(objkey)) {
      keyList.push(objkey);
    }
  }

  return keyList;
} // 比较object


function equalObjects(value, other) {
  var valueKeys = getObjectKeys(value);
  var otherKeys = getObjectKeys(other);
  var valueLenght = valueKeys.length;
  var otherLenght = otherKeys.length;

  if (valueLenght !== otherLenght) {
    return false;
  }

  var index = -1;
  var result = true;

  while (++index < valueLenght) {
    if (!(value[valueKeys[index]] === other[valueKeys[index]] || isEqual(value[valueKeys[index]], other[valueKeys[index]]))) {
      result = false;
      break;
    }
  }

  return result;
}

function isEqual(value, other) {
  /**
   * 1. 判断相等
   * 返回：true
   */
  if (value === other) {
    return true;
  }
  /**
   * 2. 判断类型
   * 不同，返回：false
   */


  var valType = type(value);
  var othType = type(other);
  var isSameType = valType === othType;

  if (!isSameType) {
    return false;
  }
  /**
   * 3. value类型为：Nan
   */


  if (value !== value && other !== other) {
    return true;
  }
  /**
   * 4. value类型为：buffer，判断是否相同
   */


  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(value)) {
    return value.equals(other);
  }
  /**
   * 5.
   */


  switch (valType) {
    case 'boolean':
    case 'date':
    case 'number':
      return +value === +other;

    case 'string':
    case 'regexp':
      return String(value) === String(other);

    case 'array':
      return equalArrays(value, other);

    case 'map':
      return equalArrays(mapToArray(value), mapToArray(other));

    case 'set':
      return equalArrays(setToArray(value), setToArray(other));

    case 'arraybuffer':
      if (value.byteLength !== other.byteLength || !equalArrays(new Uint8Array(value), new Uint8Array(other))) {
        return false;
      }

      return true;

    case 'dataview':
      return isEqual(value.buffer, other.buffer);

    case 'object':
      return equalObjects(value, other);
  }

  return false;
}

export { isInRange, isDecimalLenInRange, isStrLenInRange, isNumber, isInteger, isInt, isBoolean, isString, isEmptyString, isNull, isUndefined, isObject, isFunction, isArray, isMobilephoneNumber, isTelephoneNumber, isPhoneNumber, isPhoneNumberHK, isIdCard, isIdCardHK, isBankCard, isEqual };
